import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./MatchDrawings.css";

const PAIRS = [
  {
    id: "toothbrush",
    target: "🪥",
    targetLabel: "Cepillo de dientes",
    answer: "🦷",
    answerLabel: "Diente",
    category: "Uso funcional",
  },
  {
    id: "hammer",
    target: "🔨",
    targetLabel: "Martillo",
    answer: "🔩",
    answerLabel: "Clavo",
    category: "Herramientas",
  },
  {
    id: "rain",
    target: "🌧️",
    targetLabel: "Lluvia",
    answer: "☂️",
    answerLabel: "Paraguas",
    category: "Protección",
  },
  {
    id: "scissors",
    target: "✂️",
    targetLabel: "Tijeras",
    answer: "📄",
    answerLabel: "Papel",
    category: "Uso funcional",
  },
  {
    id: "lock",
    target: "🔒",
    targetLabel: "Candado",
    answer: "🔑",
    answerLabel: "Llave",
    category: "Complementos",
  },
  {
    id: "pencil",
    target: "✏️",
    targetLabel: "Lápiz",
    answer: "📒",
    answerLabel: "Cuaderno",
    category: "Material escolar",
  },
  {
    id: "headphones",
    target: "🎧",
    targetLabel: "Auriculares",
    answer: "🎵",
    answerLabel: "Música",
    category: "Uso funcional",
  },
  {
    id: "car",
    target: "🚗",
    targetLabel: "Coche",
    answer: "⛽",
    answerLabel: "Combustible",
    category: "Funcionamiento",
  },
  {
    id: "dog",
    target: "🐕",
    targetLabel: "Perro",
    answer: "🦴",
    answerLabel: "Hueso",
    category: "Asociación",
  },
  {
    id: "cat",
    target: "🐈",
    targetLabel: "Gato",
    answer: "🐟",
    answerLabel: "Pez",
    category: "Asociación",
  },
  {
    id: "baby",
    target: "👶",
    targetLabel: "Bebé",
    answer: "🍼",
    answerLabel: "Biberón",
    category: "Uso funcional",
  },
  {
    id: "candle",
    target: "🕯️",
    targetLabel: "Vela",
    answer: "🔥",
    answerLabel: "Fuego",
    category: "Funcionamiento",
  },
  {
    id: "sun",
    target: "☀️",
    targetLabel: "Sol",
    answer: "🕶️",
    answerLabel: "Gafas de sol",
    category: "Protección",
  },
  {
    id: "snow",
    target: "❄️",
    targetLabel: "Nieve",
    answer: "🧤",
    answerLabel: "Guantes",
    category: "Protección",
  },
  {
    id: "soap",
    target: "🧼",
    targetLabel: "Jabón",
    answer: "👐",
    answerLabel: "Manos",
    category: "Higiene",
  },
  {
    id: "comb",
    target: "🪮",
    targetLabel: "Peine",
    answer: "💇",
    answerLabel: "Cabello",
    category: "Higiene",
  },
  {
    id: "pan",
    target: "🍳",
    targetLabel: "Sartén",
    answer: "🥚",
    answerLabel: "Huevo",
    category: "Cocina",
  },
  {
    id: "spoon",
    target: "🥄",
    targetLabel: "Cuchara",
    answer: "🥣",
    answerLabel: "Cuenco",
    category: "Cocina",
  },
  {
    id: "knife",
    target: "🔪",
    targetLabel: "Cuchillo",
    answer: "🍎",
    answerLabel: "Manzana",
    category: "Uso funcional",
  },
  {
    id: "watering_can",
    target: "🚿",
    targetLabel: "Agua",
    answer: "🌱",
    answerLabel: "Planta",
    category: "Cuidado",
  },
  {
    id: "camera",
    target: "📷",
    targetLabel: "Cámara",
    answer: "🖼️",
    answerLabel: "Fotografía",
    category: "Resultado",
  },
  {
    id: "phone",
    target: "📱",
    targetLabel: "Teléfono",
    answer: "📞",
    answerLabel: "Llamada",
    category: "Uso funcional",
  },
  {
    id: "letter",
    target: "✉️",
    targetLabel: "Carta",
    answer: "📮",
    answerLabel: "Buzón",
    category: "Uso funcional",
  },
  {
    id: "book",
    target: "📖",
    targetLabel: "Libro",
    answer: "👓",
    answerLabel: "Gafas",
    category: "Uso funcional",
  },
  {
    id: "football",
    target: "⚽",
    targetLabel: "Balón",
    answer: "🥅",
    answerLabel: "Portería",
    category: "Deporte",
  },
  {
    id: "tennis",
    target: "🎾",
    targetLabel: "Pelota de tenis",
    answer: "🎾",
    answerLabel: "Raqueta de tenis",
    displayAnswer: "🏸",
    category: "Deporte",
  },
  {
    id: "paint",
    target: "🎨",
    targetLabel: "Pintura",
    answer: "🖌️",
    answerLabel: "Pincel",
    category: "Material artístico",
  },
  {
    id: "needle",
    target: "🪡",
    targetLabel: "Aguja",
    answer: "🧵",
    answerLabel: "Hilo",
    category: "Complementos",
  },
  {
    id: "plug",
    target: "🔌",
    targetLabel: "Enchufe",
    answer: "💡",
    answerLabel: "Bombilla",
    category: "Funcionamiento",
  },
  {
    id: "bed",
    target: "🛏️",
    targetLabel: "Cama",
    answer: "😴",
    answerLabel: "Dormir",
    category: "Uso funcional",
  },
];

const DISTRACTORS = [
  { emoji: "🍋", label: "Limón" },
  { emoji: "🚲", label: "Bicicleta" },
  { emoji: "🎈", label: "Globo" },
  { emoji: "🐘", label: "Elefante" },
  { emoji: "⌚", label: "Reloj" },
  { emoji: "🧸", label: "Osito" },
  { emoji: "🌳", label: "Árbol" },
  { emoji: "🍞", label: "Pan" },
  { emoji: "👕", label: "Camiseta" },
  { emoji: "🚪", label: "Puerta" },
  { emoji: "🪑", label: "Silla" },
  { emoji: "🥕", label: "Zanahoria" },
  { emoji: "🚌", label: "Autobús" },
  { emoji: "🏠", label: "Casa" },
  { emoji: "⭐", label: "Estrella" },
  { emoji: "🎁", label: "Regalo" },
  { emoji: "🍓", label: "Fresa" },
  { emoji: "🧢", label: "Gorra" },
  { emoji: "🐢", label: "Tortuga" },
  { emoji: "🏀", label: "Balón de baloncesto" },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 2,
    showLabels: true,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 4,
    showLabels: true,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 6,
    showLabels: false,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const getPairAnswerEmoji = (pair) =>
  pair.displayAnswer || pair.answer;

const createRound = ({
  itemCount,
  previousPairId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 2, 2),
    8
  );

  const availablePairs = PAIRS.filter(
    (pair) => pair.id !== previousPairId
  );

  const pair =
    availablePairs[
      Math.floor(
        Math.random() * availablePairs.length
      )
    ];

  const correctEmoji = getPairAnswerEmoji(pair);

  const distractorPool = shuffleArray(
    DISTRACTORS.filter(
      (item) =>
        item.emoji !== correctEmoji &&
        item.emoji !== pair.target
    )
  );

  const selectedDistractors = distractorPool.slice(
    0,
    safeItemCount - 1
  );

  const options = shuffleArray([
    {
      id: `${pair.id}-correct`,
      emoji: correctEmoji,
      label: pair.answerLabel,
      isCorrect: true,
    },
    ...selectedDistractors.map((item, index) => ({
      id: `${pair.id}-distractor-${index}`,
      emoji: item.emoji,
      label: item.label,
      isCorrect: false,
    })),
  ]);

  return {
    id: `${Date.now()}-${Math.random()}`,
    pairId: pair.id,
    target: pair.target,
    targetLabel: pair.targetLabel,
    answerLabel: pair.answerLabel,
    category: pair.category,
    options,
  };
};

function MatchDrawings() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const totalAnswers = correctAnswers + errors;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const totalErrors = calculateErrorCount(errors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,
      totalRounds:
        Number(resolvedConfig.totalRounds) || 8,
      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) || 2,
          2
        ),
        8
      ),
      showLabels:
        resolvedConfig.showLabels !== false,
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setResponseLocked(false);
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousPairId: round.pairId,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);
    setRoundStartTime(Date.now());
    setResponseLocked(false);
  };

  const handleAnswer = (option) => {
    if (!round || responseLocked) {
      return;
    }

    setResponseLocked(true);

    const responseTime =
      Date.now() - roundStartTime;

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (option.isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );
    } else {
      setErrors((current) => current + 1);
    }

    moveToNextRound();
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Emparejar dibujos"
        description="Observa el dibujo principal y selecciona el que mantiene una relación lógica con él."
        areas={[
          "Razonamiento",
          "Asociación semántica",
          "Atención selectiva",
          "Flexibilidad cognitiva",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Opciones de respuesta",
            min: 2,
            max: 8,
            defaultValue: 4,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="match-drawings-page">
        <div className="match-drawings-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalAnswers}
            accuracy={accuracy}
            errors={totalErrors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="match-drawings-results-grid">
            <div className="match-drawings-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="match-drawings-result-card">
              <span>Errores</span>
              <strong>{errors}</strong>
            </div>

            <div className="match-drawings-result-card">
              <span>Precisión</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>

            <div className="match-drawings-result-card">
              <span>Rondas realizadas</span>
              <strong>{totalAnswers}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="match-drawings-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="match-drawings-page">
      <main className="match-drawings-card">
        <header className="match-drawings-header">
          <div>
            <p className="match-drawings-area">
              Razonamiento
            </p>

            <h1>Emparejar dibujos</h1>

            <p>
              Selecciona el dibujo que tiene una
              relación lógica con el principal.
            </p>
          </div>

          <div className="match-drawings-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="match-drawings-game">
          <div className="match-drawings-target">
            <span className="match-drawings-target-title">
              Dibujo principal
            </span>

            <div className="match-drawings-target-emoji">
              {round?.target}
            </div>

            {config?.showLabels && (
              <strong>{round?.targetLabel}</strong>
            )}
          </div>

          <div className="match-drawings-connector">
            <span />
            <strong>¿Con cuál se relaciona?</strong>
            <span />
          </div>

          <div
            className="match-drawings-options"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.options.length || 2,
                4
              )}, minmax(100px, 1fr))`,
            }}
          >
            {round?.options.map((option) => (
              <button
                type="button"
                key={option.id}
                className="match-drawings-option"
                onClick={() =>
                  handleAnswer(option)
                }
                disabled={responseLocked}
                aria-label={option.label}
              >
                <span>{option.emoji}</span>

                {config?.showLabels && (
                  <strong>{option.label}</strong>
                )}
              </button>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MatchDrawings;